import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import type { RespondedType, ResponseError } from '@/models/response';
import { getUserId } from '@/stores/auth';
import { FILE_ENDPOINTS_STORAGE_KEY } from '@/utils/constants';
import { fetcher } from '@/utils/fetcher';
import { createLogger } from '@/utils/logger';

const notifyFileEndDomainResultSchema = z.union([
  z.literal('health'),
  z.literal('fetch'),
  z.literal('display'),
  z.literal(true),
]);

const notifyFileEndDomainStoreSchema = z.record(z.string(), notifyFileEndDomainResultSchema);
type NotifyFileEndDomainResult = z.infer<typeof notifyFileEndDomainResultSchema>;

interface NotifyFileEndDomainChangeParams {
  domain: string;
  result: NotifyFileEndDomainResult;
}

export async function notifyFileEndDomainChange({
  domain,
  result,
}: NotifyFileEndDomainChangeParams): Promise<RespondedType> {
  if (!getUserId()) throw 'NOT_AUTHENTICATED';

  const data: Record<string, NotifyFileEndDomainResult> = await notifyFileEndDomainStoreSchema
    .parseAsync(JSON.parse(sessionStorage.getItem(FILE_ENDPOINTS_STORAGE_KEY) || '{}'))
    .catch(() => ({}));
  sessionStorage.setItem(
    FILE_ENDPOINTS_STORAGE_KEY,
    JSON.stringify({
      ...(data ?? {}),
      [domain]: result,
    }),
  );

  if (data[domain] !== true) {
    return Promise.resolve({ Code: 0, Msg: 'OK' });
  }

  let value: string;

  switch (result) {
    case 'fetch':
      value = `Fail-Fetch-${domain}`;
      break;
    case 'health':
      value = `Fail-Health-${domain}`;
      break;
    case 'display':
      value = `Fail-Display-${domain}`;
      break;
    default:
      value = domain;
      break;
  }

  dynamicEndpointLogger.info('NotifyFileEndPointDomainChange', result, domain);
  return fetcher<RespondedType>('/NotifyFileEndPointDomainChange', {
    Domain: value,
  });
}

const dynamicEndpointLogger = createLogger('Dynamic Endpoint');

export default function useNotifyFileEndDomainChange() {
  return useMutation<RespondedType, ResponseError, NotifyFileEndDomainChangeParams>({
    mutationKey: ['/NotifyFileEndPointDomainChange'],
    mutationFn: (params) => notifyFileEndDomainChange(params),
    onError: undefined,
  });
}
